import { PanResponder, View, type AccessibilityProps } from "@-/com.npmjs.react-native";
import useLatest from "@-/com.npmjs.use-latest";
import { useMemo, type ReactNode } from "react";

/** The assistive-technology equivalent of the swipe, exposed as a custom action on the trigger. */
export interface SwipeDownGestureAction {
  /** Spoken hint on the trigger, e.g. "Swipe down to open the debug panel". */
  hint: string;
  /** The action's label, e.g. "Open debug panel". */
  label: string;
  /** The action's stable name, e.g. "debug". */
  name: string;
}

/**
 * A hidden secondary gesture on a button: swiping down from it calls `onSwipeDown`, while ordinary
 * taps pass through to the button. Only the wrapped touch target owns the gesture, so a vertical
 * drag anywhere else on the screen is unaffected.
 *
 * `children` receives the accessibility props to spread onto the button, which expose the swipe as
 * a custom action while `enabled` (and nothing while it is not).
 */
export function SwipeDownGesture({
  action,
  activation = "release",
  children,
  className,
  enabled,
  onSwipeDown,
  testID,
}: {
  action: SwipeDownGestureAction;
  /**
   * When `onSwipeDown` fires: `release` (default) once the finger lifts after a long enough swipe;
   * `intent` as soon as the drag is recognized as a downward swipe, for surfaces that should
   * start opening while the finger is still moving.
   */
  activation?: "intent" | "release";
  children: (props: AccessibilityProps) => ReactNode;
  className?: string;
  enabled: boolean;
  onSwipeDown: () => void;
  testID?: string;
}) {
  const onSwipeDownRef = useLatest(onSwipeDown);
  const gesture = useMemo(
    () =>
      PanResponder.create({
        onMoveShouldSetPanResponderCapture: (_event, { dx, dy }) =>
          enabled && dy > 12 && dy > Math.abs(dx) * 1.5,
        // Granted only by the capture above, i.e. once the drag reads as a downward swipe.
        onPanResponderGrant: () => {
          if (enabled && activation === "intent") {
            onSwipeDownRef.current();
          }
        },
        onPanResponderRelease: (_event, { dx, dy }) => {
          if (enabled && activation === "release" && dy >= 36 && dy > Math.abs(dx) * 1.5) {
            onSwipeDownRef.current();
          }
        },
        onPanResponderTerminationRequest: () => false,
      }),
    [activation, enabled, onSwipeDownRef],
  );
  return (
    <View {...gesture.panHandlers} className={className} testID={testID}>
      {children(
        enabled
          ? {
              accessibilityActions: [{ label: action.label, name: action.name }],
              accessibilityHint: action.hint,
              onAccessibilityAction: ({ nativeEvent }) => {
                if (nativeEvent.actionName === action.name) {
                  onSwipeDownRef.current();
                }
              },
            }
          : {},
      )}
    </View>
  );
}
